import { createTrackingConfig } from '../../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../../config/appConfig';
import CustomMyApprovalsSplitView from './CustomMyApprovalsSplitView';

export const myApprovalsConfig = {
  entityName: 'My Approvals',
  
  // Entity information for file attachments and audit logs
  entityTypeId: 9, // My Approvals entity type ID
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/approval-requests/my-approvals',
    endpoints: {
      list: '',
      getById: (id) => `/${id}`,
      approve: (id) => `/${id}/approve`,
      reject: (id) => `/${id}/reject`,
      delegate: (id) => `/${id}/delegate`
    },
    lookupEndpoints: {
      users: '/users',
      departments: '/department'
    },
    // SWR invalidation endpoints - for refreshing data after operations
    swrInvalidation: {
      mainList: '/approval-requests/my-approvals',
      myApprovals: '/approval-requests/my-approvals'
    }
  },

  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '30%', // 30% of table width 
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle, // From app config: 'badge'
    tableVariant: 'default', // 'default' | 'modern' | 'cards' | 'board' | 'timeline' 
                            // default: default table with cards
                            // cards: cards table
                            // board: board table
                            // timeline: timeline table
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth, // From app config: '60%'
    // Disable all action buttons for My Approvals (read-only for approvers)
    showViewButton: true,
    showEditButton: false,
    showDeleteButton: false,
    showActionsColumn: true,
    showAddButton: false // Disable add button for My Approvals (approvers can't create new approvals)
  },

  // Features configuration
  features: {
    fileAttachments: true, // Enable file attachment functionality
    masterEntityTypeId: 9, // My Approvals master entity type ID for audit logs
    approvalRequests: true, // Enable approval request functionality
  },

  // Custom Split View Panel configuration for My Approvals module
  customSplitViewPanel: {
    enabled: true,
    component: CustomMyApprovalsSplitView  // Use the custom My Approvals split view component
  },

  // UNIFIED FIELD CONFIGURATION - Single source of truth
  fields: [
    // Basic Information
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    // Code
    { 
      name: 'code', 
      label: 'Code', 
      type: 'text', 
      required: true,
      placeholder: 'Enter approval code',
      table: false, // Remove from table display
      filterable: true,
      validation: {
        required: true,
        pattern: /^[A-Z0-9-]+$/,
        message: 'Approval code must contain only uppercase letters, numbers, and hyphens'
      }
    },
    // Title
    { 
      name: 'title', 
      label: 'Approval Title', 
      type: 'text', 
      required: true,
      placeholder: 'Enter approval title',
      table: { header: 'Title', width: '300px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        minLength: 3,
        maxLength: 255
      }
    },
    // Description
    { 
      name: 'description', 
      label: 'Description', 
      type: 'textarea',
      placeholder: 'Enter approval description',
      rows: 3,
      table: false, // Remove from table display
      filterable: false
    },
    // Requested At
    { 
      name: 'requestedAt', 
      label: 'Requested At', 
      type: 'date',
      readonly: true,
      table: { 
        header: 'Requested', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange'
    },
    // Requested By
    { 
      name: 'requestedBy', 
      label: 'Requested By', 
      type: 'computed',
      table: { 
        header: 'Requested By', 
        width: '150px', 
        sortable: true,
        cellRenderer: 'userDisplay'
      },
      filterable: true,
      filterType: 'text'
    },
    // Status
    { 
      name: 'status', 
      label: 'Status', 
      type: 'select', 
      required: true,
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'cancelled', label: 'Cancelled' }
      ],
      table: { 
        header: 'Status', 
        width: '120px', 
        sortable: true,
        cellRenderer: 'statusBadge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
      }
    },
    // Priority
    { 
      name: 'priority', 
      label: 'Priority', 
      type: 'select', 
      required: true,
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'urgent', label: 'Urgent' }
      ],
      table: { 
        header: 'Priority', 
        width: '100px', 
        sortable: true,
        cellRenderer: 'priorityBadge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
      }
    },
    // Entity ID
    { 
      name: 'entityId', 
      label: 'Entity ID', 
      type: 'number',
      placeholder: 'Enter entity ID',
      table: false, // Hide raw ID from table
      filterable: true,
      validation: {
        required: false,
        min: 1
      }
    },
    // Entity Type (computed field)
    { 
      name: 'entityType', 
      label: 'Entity Type', 
      type: 'computed',
      table: { 
        header: 'Entity', 
        width: '120px', 
        sortable: false,
        cellRenderer: 'entityTypeBadge'
      },
      filterable: false
    },
    // Entity Details (computed field)
    { 
      name: 'entityDetails', 
      label: 'Entity Details', 
      type: 'computed',
      table: { 
        header: 'Entity Details', 
        width: '200px', 
        sortable: false,
        cellRenderer: 'entityDetails'
      },
      filterable: false
    },
    // File Attachments (for entityTypeId 1 or 2)
    { 
      name: 'fileAttachments', 
      label: 'Files', 
      type: 'computed',
      table: { 
        header: 'Files', 
        width: '150px', 
        sortable: false,
        cellRenderer: 'myApprovalsFileCount'
      },
      filterable: false,
      computedValue: (record) => {
        // Return file count for sorting/filtering if needed
        return record.fileAttachments?.length || 0;
      }
    },
    // Workflow Type
    { 
      name: 'workflowType', 
      label: 'Workflow Type', 
      type: 'select',
      options: [
        { value: 'review', label: 'Review Only' },
        { value: 'approval', label: 'Approval Only' },
        { value: 'review_approval', label: 'Review & Approval' }
      ],
      table: { 
        header: 'Workflow', 
        width: '120px', 
        sortable: true,
        cellRenderer: 'workflowTypeBadge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    // Approval Type
    { 
      name: 'approvalType', 
      label: 'Approval Type', 
      type: 'select', 
      required: true,
      options: [
        { value: 'single', label: 'Single' },
        { value: 'sequential', label: 'Sequential' },
        { value: 'parallel', label: 'Parallel' },
        { value: 'any_one', label: 'Any One' }
      ],
      table: { 
        header: 'Type', 
        width: '100px', 
        sortable: true,
        cellRenderer: 'approvalTypeBadge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
      }
    },
    // Approval Progress (calculated from steps)
    { 
      name: 'approvalProgress', 
      label: 'Approval Progress', 
      type: 'computed',
      table: { 
        header: 'Progress', 
        width: '180px', 
        sortable: false,
        cellRenderer: 'approvalProgress'
      },
      filterable: false,
      // Progress is calculated from steps:
      // - Counts steps with approvedAt, reviewedAt, or rejectedAt
      // - Only counts required steps (isRequired !== false)
      // - Format: "X of Y completed" or "X/Y"
      // - Shows rejection status if any step is rejected
      computedValue: (record) => {
        if (!record.steps || record.steps.length === 0) {
          return 'No steps';
        }
        
        // Filter to only required steps
        const requiredSteps = record.steps.filter(step => step.isRequired !== false);
        const allRequiredSteps = requiredSteps.length > 0 ? requiredSteps : record.steps;
        
        // Check for rejected steps
        const hasRejectedStep = allRequiredSteps.some(step => step.rejectedAt || step.rejectedBy);
        if (hasRejectedStep) {
          return '❌ Rejected';
        }
        
        // Count completed steps based on approvalType
        const isStepCompleted = (step) => {
          if (step.stepType === 'review') {
            return !!(step.reviewedAt || step.reviewedBy) && !step.rejectedAt;
          }
          return !!(step.approvedAt || step.approvedBy) && !step.rejectedAt;
        };
        
        let completedCount = 0;
        let totalRequired = allRequiredSteps.length;
        
        switch (record.approvalType) {
          case 'single':
            completedCount = allRequiredSteps.filter(isStepCompleted).length;
            break;
          case 'sequential':
            let seqCompleted = 0;
            for (let i = 0; i < allRequiredSteps.length; i++) {
              if (isStepCompleted(allRequiredSteps[i])) {
                seqCompleted++;
              } else {
                break;
              }
            }
            completedCount = seqCompleted;
            break;
          case 'parallel':
            completedCount = allRequiredSteps.filter(isStepCompleted).length;
            break;
          case 'any_one':
            const firstCompleted = allRequiredSteps.find(isStepCompleted);
            completedCount = firstCompleted ? 1 : 0;
            totalRequired = 1;
            break;
          case 'review_approval':
            const reviewSteps = allRequiredSteps.filter(s => s.stepType === 'review');
            const approvalSteps = allRequiredSteps.filter(s => s.stepType === 'approval');
            const reviewedCount = reviewSteps.filter(s => s.reviewedAt).length;
            const approvedCount = approvalSteps.filter(s => s.approvedAt).length;
            completedCount = reviewedCount + approvedCount;
            totalRequired = reviewSteps.length + approvalSteps.length;
            break;
          default:
            completedCount = allRequiredSteps.filter(isStepCompleted).length;
        }
        
        const percentage = totalRequired > 0 ? Math.round((completedCount / totalRequired) * 100) : 0;
        const isComplete = completedCount >= totalRequired;
        
        return isComplete 
          ? `✅ ${completedCount}/${totalRequired}`
          : `${completedCount}/${totalRequired} (${percentage}%)`;
      }
    },

    // Steps (for detailed view)
    { 
      name: 'steps', 
      label: 'Approval Steps', 
      type: 'computed',
      table: false, // Hide from table, show in details
      filterable: false
    },
    // Current Step Info
    { 
      name: 'currentStepInfo', 
      label: 'Current Step', 
      type: 'computed',
      table: false,
      filterable: false
    },
    // Approvers Summary
    { 
      name: 'approversSummary', 
      label: 'Approvers', 
      type: 'computed',
      table: false,
      filterable: false
    },
    // Due Date
    { 
      name: 'dueDate', 
      label: 'Due Date', 
      type: 'date',
      table: { 
        header: 'Due Date', 
        width: tableConfig.dateColumnWidth, // From app config: '120px'
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table') // From app config: 'dd/MM/yyyy'
      },
      filterable: true,
      filterType: 'dateRange'
    },
  ],

  // COMPUTED CONFIGURATIONS - Generated from unified fields (no maintenance needed!)
  get tableColumns() {
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,  // Use displayField as key if available
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name  // Keep reference to original field name
      }));
    
    return columns;
  },

  exportFileName: 'my_approvals',
  
  // Custom handlers for my approvals-specific business logic
  customHandlers: {
    onSave: null, // Will be defined in myApprovalsPage
    onDelete: null, // Will be defined in myApprovalsPage
    onLoad: null, // Will be defined in myApprovalsPage
    onFormOpen: null, // Will be defined in myApprovalsPage
    
    // Approval action handlers
    onApprove: null, // Will handle approval action
    onReject: null, // Will handle rejection action
    onDelegate: null // Will handle delegation action
  },
  
  // Data transformation before submit to ensure correct types for backend/Prisma
  transformData: (data) => {
    const transformed = { ...data };

    // Numeric fields
    const intFields = [
      'entityId', 'currentStep', 'totalSteps', 'requestedById', 'approvedById',
      'createdById', 'updatedById'
    ];
    intFields.forEach((field) => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const n = parseInt(transformed[field], 10);
          if (!Number.isNaN(n)) transformed[field] = n; else delete transformed[field];
        }
      }
    });

    // Dates
    const dateFields = ['dueDate', 'requestedAt', 'approvedAt', 'createdAt', 'updatedAt'];
    dateFields.forEach((field) => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const d = new Date(transformed[field]);
          if (!Number.isNaN(d.getTime())) transformed[field] = d; else delete transformed[field];
        }
      }
    });

    return transformed;
  },
  
  // Backend field mappings
  fieldMappings: {
    // Map frontend display fields to backend field paths
    requestedBy: 'requestedBy.userDisplayName',
    approvedBy: 'approvedBy.userDisplayName',
    createdBy: 'createdBy.userDisplayName',
    updatedBy: 'updatedBy.userDisplayName'
  },
  
  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['title', 'code', 'status', 'priority', 'entityType', 'approvalType', 'workflowType']
    },
    {
      name: 'advanced',
      label: 'Advanced', 
      icon: '⚙️',
      fields: ['dueDate', 'requestedAt', 'approvedAt', 'entityId']
    }
  ],

  // Custom Sort Orders - Define logical sorting for non-database categorical fields
  customSortOrders: {
    // Status values
    status: ['pending', 'approved', 'rejected', 'cancelled'],
    priority: ['low', 'medium', 'high', 'urgent'],
    approvalType: ['single', 'sequential', 'parallel', 'any_one'],
    workflowType: ['review', 'approval', 'review_approval']
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },
  
  // Notification messages
  messages: {
    loading: 'Loading my approvals...',
    noData: 'No approvals found. You have no pending approvals.',
    approveSuccess: 'Approval approved successfully!',
    rejectSuccess: 'Approval rejected successfully!',
    delegateSuccess: 'Approval delegated successfully!',
    approveError: 'Failed to approve',
    rejectError: 'Failed to reject',
    delegateError: 'Failed to delegate',
    loadError: 'Failed to load approvals',
    validationError: 'Validation failed'
  },
  
  // Form Layout Configuration - Single source of truth
  formSections: [
    {
      title: 'Basic Information',
      icon: '📋',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['code', 'title', 'description'],
      fieldSpans: {
        'code': 'md:col-span-4',
        'title': 'md:col-span-8', 
        'description': 'md:col-span-12'
      }
    },
    {
      title: 'Approval Details',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-3',
      fields: ['status', 'priority', 'approvalType']
    },
    {
      title: 'Entity Information',
      icon: '🔗',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['entityType', 'entityId'],
      fieldSpans: {
        'entityType': 'md:col-span-6',
        'entityId': 'md:col-span-6'
      }
    },
    {
      title: 'Timeline',
      icon: '⏰',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['dueDate', 'requestedAt', 'approvedAt'],
      fieldSpans: {
        'dueDate': 'md:col-span-4',
        'requestedAt': 'md:col-span-4',
        'approvedAt': 'md:col-span-4'
      }
    },
    {
      title: 'Comments & Notes',
      icon: '💬',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['comments', 'rejectionReason'],
      fieldSpans: {
        'comments': 'md:col-span-6',
        'rejectionReason': 'md:col-span-6'
      }
    }
  ],

  // Tracking Section Configuration (automatically generated from centralized labels)
  trackingSection: createTrackingConfig('My Approvals'),

  // Labels and Text Configuration (uses predefined my approvals labels + custom overrides)
  labels: {
    // Using predefined my approvals labels from centralized labels file
    // You can override any label by uncommenting and modifying:
  },

  // Centralized field help text (MVP: short tooltips)
    helpText: {
    code: { short: 'Unique code for this approval request.' },
    title: { short: 'Human-readable title for the approval request.' },
    description: { short: 'Detailed description of what needs approval.' },
    status: { short: 'Current status of the approval (Pending/Approved/Rejected/Cancelled).' },
    priority: { short: 'Priority level (Low/Medium/High/Urgent).' },
    entityType: { short: 'Type of entity being approved (Document/CAPA/Change Request/etc.).' },
    entityId: { short: 'ID of the specific entity being approved.' },
    approvalType: { short: 'Approval workflow type (Single/Sequential/Parallel/Any One).' },
    workflowType: { short: 'Workflow process type (Review Only/Approval Only/Review & Approval).' },
    steps: { short: 'Individual approval steps and their current status.' },
    currentStepInfo: { short: 'Information about the current step in the approval process.' },
    approversSummary: { short: 'Summary of all approvers and their current status.' },
    approvalProgress: { short: 'Progress calculated from completed steps (based on approvedAt/reviewedAt timestamps).' },
    dueDate: { short: 'Date by which approval must be completed.' }
  },

  // Fields to remove during data preparation for submission (used by BaseService)
  fieldsToRemove: [
    'requestedBy', 'approvedBy', 'createdBy', 'updatedBy',
    'steps', 'notifications', 'fileAttachments'
  ],

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null, // Will be set after validation module loads

  // Helper methods for approval functionality
  isApprovalEnabled: function() {
    return this.features.approvalRequests;
  },

  canApprove: function(approval) {
    return approval && approval.status === 'pending';
  },

  canReject: function(approval) {
    return approval && approval.status === 'pending';
  },

  canDelegate: function(approval) {
    return approval && approval.status === 'pending';
  }
};
